import { BuilderElement, GlobalConfig } from "@/types/builder";
import { generateId } from "@/utils/builder";
import {
  createMentionedInSection,
  createPartnerMenuSection,
  createListingSection,
  createFooterMenuSection,
  createFooterEndSection,
  createFAQSection,
} from "./template2";

// ====================== Header Section ======================
const createHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    backgroundColor: "background",
    padding: "14px 0",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "20px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1496px",
        margin: "0 auto",
        width: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: "0 50px",
      },
      children: [
        {
          id: generateId(),
          type: "logo" as const,
          styles: {
            maxWidth: "136px",
            display: "flex",
            alignItems: "center",
          },
          responsiveStyles: {
            mobile: {
              maxWidth: "90px",
            },
          },
          children: [
            {
              id: generateId(),
              type: "image" as const,
              src: "/images/movyo/logo.webp",
              alt: "Movyo Logo",
              styles: {
                width: "100%",
                height: "100%",
                objectFit: "contain",
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Terms Header Section ======================
const createTermsHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "93px 0",
    backgroundColor: "#F7F7F7",
    backdropFilter: "blur(14.3px)",
    color: "forground",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "60px 0",
    },
    mobile: {
      padding: "40px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1382px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "10px",
          },
          responsiveStyles: {
            tablet: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
            mobile: {
              flexDirection: "column",
              gap: "10px",
              justifyContent: "flex-start",
              alignItems: "flex-start",
            },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as const,
              content: "Terms and Conditions",
              styles: {
                textAlign: "left",
                fontFamily: "Manrope, sans-serif",
                fontSize: "70px",
                fontStyle: "normal",
                fontWeight: "800",
                lineHeight: "80px",
                letterSpacing: "-2.1px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "60px",
                  lineHeight: "60px",
                },
                tablet: {
                  fontSize: "50px",
                  lineHeight: "50px",
                },
                mobile: {
                  fontSize: "45px",
                  lineHeight: "45px",
                  fontWeight: "700",
                },
              },
            },
            {
              id: generateId(),
              type: "heading" as const,
              content: "Date Effective: April 13, 2025",
              styles: {
                textAlign: "center",
                fontFamily: "Manrope, sans-serif",
                fontSize: "40px",
                fontStyle: "normal",
                fontWeight: "600",
                lineHeight: "80px",
                letterSpacing: "-1.2px",
                margin: "0",
              },
              responsiveStyles: {
                desktop: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                tablet: {
                  fontSize: "34px",
                  lineHeight: "34px",
                },
                mobile: {
                  textAlign: "left",
                  fontSize: "25px",
                  lineHeight: "25px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Terms Content Section ======================
const createTermsContentSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as const,
  styles: {
    padding: "120px 0 104px",
    backgroundColor: "background",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "80px 0",
    },
    mobile: {
      padding: "60px 0",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as const,
      styles: {
        maxWidth: "1339px",
        margin: "0 auto",
        width: "100%",
        padding: "0 50px",
      },
      responsiveStyles: {
        mobile: {
          padding: "0 20px",
        },
        tablet: {
          padding: "0 20px",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as const,
          styles: {
            display: "flex",
            flexDirection: "column",
            gap: "60px 0",
            color: "forground",
          },
          responsiveStyles: {
            tablet: {
              gap: "40px 0",
            },
          },
          children: [
            // Terms Introduction
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "Terms and Conditions",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "55px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.55px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "45px",
                      lineHeight: "45px",
                    },
                    mobile: {
                      fontSize: "40px",
                      lineHeight: "40px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    'Welcome to Movyo.net ("we," "us," or "our"). By accessing this website and/or purchasing any products from us, you agree to be bound by the following Terms and Conditions. Please read them carefully. If you do not agree with any part of these terms, please do not use our website.',
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 1: Use of This Website
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "1. Use of This Website",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "By using Movyo.net, you confirm that you are at least 18 years old and legally capable of entering into a binding contract. You agree to use this website only for lawful purposes and in a way that does not infringe the rights of others or restrict their use and enjoyment of the site.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "You are not permitted to modify, copy, distribute, reproduce, publish, license, or sell any information, products, or services obtained from this site without our written consent.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Section 2: Product Information and Medical Disclaimer
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "2. Product Information and Medical Disclaimer",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "Our products are dietary supplements and are not intended to diagnose, treat, cure, or prevent any disease. The information on this website is provided for general informational purposes only and is not medical advice. Always consult with a qualified healthcare professional before starting any new supplement, especially if you are pregnant, nursing, taking medication, or have a medical condition.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "Statements on this website have not been evaluated by the Food and Drug Administration (FDA).",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Section 3: Order Acceptance and Payment
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "3. Order Acceptance and Payment",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "By placing an order, you agree that all information provided is accurate, complete, and current. We reserve the right to refuse or cancel any order at our sole discretion, including but not limited to: pricing errors, suspicion of fraud, or product availability.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "All payments are processed securely via DigiStore24 or another trusted third-party payment provider. Movyo.net does not store or have direct access to your payment information.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Section 4: Shipping and Delivery
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "4. Shipping and Delivery",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "Orders are typically processed within 1–3 business days. Delivery times may vary based on location and shipping method selected at checkout. For international orders, additional customs fees or taxes may apply, which are the responsibility of the buyer.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 5: Refund Policy
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "5. Refund Policy",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "Your satisfaction matters to us. If you're not happy with your purchase, please review our full Refund Policy (linked in the footer of our site) for instructions on how to request a return or refund. Eligibility may depend on the time since purchase and condition of the product.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 6: Intellectual Property
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "6. Intellectual Property",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as const,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px 0",
                  },
                  responsiveStyles: {
                    desktop: {
                      gap: "18px 0",
                    },
                    mobile: {
                      gap: "15px 0",
                    },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "All content on this website—including text, graphics, images, video, logos, product names, and trademarks—is the property of Movyo.net or its licensors and is protected by copyright, trademark, and other intellectual property laws.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as const,
                      content:
                        "You may not copy, reproduce, or use any content from this site without written permission, except for personal, non-commercial use.",
                      styles: {
                        color: "forground",
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "28px",
                        fontStyle: "normal",
                        fontWeight: "400",
                        lineHeight: "40px",
                        letterSpacing: "-0.28px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        desktop: {
                          fontSize: "24px",
                          lineHeight: "36px",
                        },
                        mobile: {
                          fontSize: "18px",
                          lineHeight: "24px",
                        },
                      },
                    },
                  ],
                },
              ],
            },
            // Section 7: Limitation of Liability
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "7. Limitation of Liability",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "To the maximum extent permitted by law, Movyo.net and its owners, affiliates, and employees will not be liable for any direct, indirect, incidental, or consequential damages resulting from your use of this website or products purchased through it. This includes, but is not limited to, loss of income, personal injury, or data loss, even if we were advised such damages were possible.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 8: Indemnification
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "8. Indemnification",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "You agree to indemnify and hold harmless Movyo.net, its affiliates, officers, employees, and agents from any claims, damages, liabilities, and expenses arising from your use of the website, your violation of these Terms, or your infringement of any intellectual property or other rights.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 9: Third-Party Links
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "9. Third-Party Links",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "This website may contain links to third-party websites or services. We are not responsible for the content, terms, or privacy practices of those sites. Use of third-party sites is at your own risk.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 10: Modifications
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "10. Modifications",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "We reserve the right to modify these Terms and Conditions at any time. Changes will be effective immediately upon posting to this page. Your continued use of the site constitutes your acceptance of any modifications.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 11: Governing Law
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "11. Governing Law",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes arising out of these Terms will be handled in the appropriate courts located in the U.S.",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
            // Section 12: Contact Information
            {
              id: generateId(),
              type: "container" as const,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "30px 0",
              },
              responsiveStyles: {
                tablet: {
                  gap: "22px 0",
                },
                mobile: {
                  gap: "18px 0",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as const,
                  content: "12. Contact Information",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "40px",
                    fontStyle: "normal",
                    fontWeight: "700",
                    lineHeight: "40px",
                    letterSpacing: "-0.4px",
                    margin: "0",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    desktop: {
                      fontSize: "34px",
                      lineHeight: "34px",
                    },
                    mobile: {
                      fontSize: "30px",
                      lineHeight: "30px",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as const,
                  content:
                    "If you have any questions about these Terms or anything else on our site, feel free to reach out: 📧 support@movyo.net",
                  styles: {
                    color: "forground",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "28px",
                    fontStyle: "normal",
                    fontWeight: "400",
                    lineHeight: "40px",
                    letterSpacing: "-0.28px",
                    margin: "0",
                  },
                  responsiveStyles: {
                    desktop: {
                      fontSize: "24px",
                      lineHeight: "36px",
                    },
                    mobile: {
                      fontSize: "18px",
                      lineHeight: "24px",
                    },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

export const template2Terms = (globalConfig: GlobalConfig) => {
  return {
    header: createHeaderSection(globalConfig) as BuilderElement,
    termsHeader: createTermsHeaderSection(globalConfig) as BuilderElement,
    termsContent: createTermsContentSection(globalConfig) as BuilderElement,
    footerMenu: createFooterMenuSection(globalConfig) as BuilderElement,
    footerEnd: createFooterEndSection(globalConfig) as BuilderElement,
  };
};

export const template2TermsGlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "Poppins, sans-serif",
      fontSize: "48px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "Poppins, sans-serif",
      fontSize: "36px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-3",
      fontFamily: "Poppins, sans-serif",
      fontSize: "24px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-4",
      fontFamily: "Poppins, sans-serif",
      fontSize: "20px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-5",
      fontFamily: "Poppins, sans-serif",
      fontSize: "18px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Heading-6",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "Poppins, sans-serif",
      fontSize: "16px",
      fontWeight: "600",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#FFFFFF",
    forground: "#333333",
    primary: "#667eea",
    secondary: "#764ba2",
    tertiary: "#f8f9fa",
    quaternary: "#e0e0e0",
    link: "#667eea",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};
